// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect, useState } from 'react';
import { useSessionGuardStore } from '../stores/sessionGuardStore';

export const IDLE_TIMEOUT_MS = 45 * 60 * 1000; // 45 min
export const IDLE_WARNING_MS = 2 * 60 * 1000; // 2 min countdown

/**
 * Drives the idle-warning countdown off `lastActivityAt`. Ticks once a
 * second (same interval idiom as `hooks/useNow.ts`) so it can recompute the
 * remaining seconds and cross the two thresholds — show the warning at 45
 * min idle, lock at 47 min idle — without a separate long-lived timer per
 * threshold.
 */
export function useIdleWarning(enabled: boolean): { warningVisible: boolean; secondsLeft: number } {
  const [secondsLeft, setSecondsLeft] = useState(IDLE_WARNING_MS / 1000);
  const warningVisible = useSessionGuardStore(s => s.warningVisible);

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => {
      const store = useSessionGuardStore.getState();
      if (store.locked) return;

      const idleMs = Date.now() - store.lastActivityAt;
      if (idleMs >= IDLE_TIMEOUT_MS + IDLE_WARNING_MS) {
        store.lock('idle');
        return;
      }
      if (idleMs >= IDLE_TIMEOUT_MS) {
        if (!store.warningVisible) store.showWarning();
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_TIMEOUT_MS + IDLE_WARNING_MS - idleMs) / 1000)));
      }
      // No "idleMs dropped back below the threshold, so dismiss the warning"
      // branch here: once the warning is visible, `useIdleActivityTracker`
      // stops recording ambient activity entirely, so `idleMs` can only grow
      // from here. The warning is dismissed by its own "Stay signed in" button,
      // which calls `dismissWarning()` directly, or by activity another tab
      // published (sessionGuardStore's `storage` listener).
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enabled]);

  return { warningVisible: enabled && warningVisible, secondsLeft };
}
